import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, TextField, Button, Stack, CircularProgress, InputAdornment, IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import { toast } from "react-toastify";
import api, { apiError, tokens } from "../api";
import { staff } from "../components/AdminHeader";

/*
 * Staff login.
 *
 * Split in two: the brand on the left, the form on the right. The left panel is not
 * decoration - a page that is only a floating box gives no clue which system you have
 * landed in, and these screens are reached by typing a URL rather than by following a
 * link from anywhere.
 *
 * What it says is deliberately thin. "Staff access only" and three lines about what is
 * inside is enough for us and tells a stranger who guessed the URL nothing they could
 * use. On a phone the panel is dropped entirely; the form is the only thing that matters
 * there.
 */

const darkField = {
    "& .MuiOutlinedInput-root": {
        color: "#fff", borderRadius: 2,
        bgcolor: "rgba(255,255,255,.04)",
        "& fieldset": { borderColor: "rgba(255,255,255,.16)" },
        "&:hover fieldset": { borderColor: "rgba(255,255,255,.32)" },
        "&.Mui-focused fieldset": { borderColor: "#7BD95F" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,.5)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#7BD95F" },
};

/* What this side of the console is for. Customers issue their own keys now, so the staff
   view is about watching, not handing out. */
const POINTS = [
    "Who has registered, and which plan they are paying for",
    "Every key issued, and which plants are still live",
    "Read what the assistant was asked, and how it answered",
];

export default function AdminLogin() {
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: "", password: "" });
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!form.email || !form.password) return;
        setBusy(true);
        try {
            const { data } = await api.post("/console/login", form);
            tokens.console.set(data.token);
            // Remembered so the header can say who is signed in without a second call.
            staff.set(data.user);
            navigate("/admin/accounts");
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    return (
        <Box sx={{
            minHeight: "100vh", color: "#fff", display: "flex",
            bgcolor: "#07120B",
        }}>
            {/* ---------- brand ---------- */}
            <Box sx={{
                display: { xs: "none", md: "flex" }, flexDirection: "column",
                width: "44%", maxWidth: 560, flexShrink: 0,
                p: 6, borderRight: "1px solid rgba(255,255,255,.08)",
                background:
                    "radial-gradient(760px 520px at 20% 0%, rgba(46,125,32,.45), transparent 62%),"
                    + "radial-gradient(620px 520px at 80% 100%, rgba(20,90,60,.4), transparent 64%),"
                    + "linear-gradient(160deg, #0B2415, #07120B)",
            }}>
                <Box component="img" src="/assets/iotmatrix-white.png" alt="iotmatrix"
                    sx={{ height: 60, width: "auto", alignSelf: "flex-start" }} />

                <Box sx={{ mt: "auto", mb: "auto" }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 38, letterSpacing: -1, lineHeight: 1.15 }}>
                        MatrixIQ Console
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.72)", fontSize: 16, mt: 1.5 }}>
                        Customers, subscriptions and the assistant running at every plant.
                    </Typography>

                    <Stack spacing={1.4} sx={{ mt: 4 }}>
                        {POINTS.map((p) => (
                            <Stack key={p} direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#7BD95F", flexShrink: 0 }} />
                                <Typography sx={{ color: "rgba(255,255,255,.8)", fontSize: 15 }}>{p}</Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Box>

                <Typography sx={{ color: "rgba(255,255,255,.4)", fontSize: 13 }}>
                    Staff access only
                </Typography>
            </Box>

            {/* ---------- form ---------- */}
            <Box sx={{ flexGrow: 1, display: "grid", placeItems: "center", p: { xs: 3, sm: 5 } }}>
                <Box sx={{ width: "100%", maxWidth: 400 }}>
                    {/* The logo rides along on a phone, where the panel beside it is gone. */}
                    <Box component="img" src="/assets/iotmatrix-white.png" alt="iotmatrix"
                        sx={{ height: 34, mb: 4, display: { xs: "block", md: "none" } }} />

                    <Typography sx={{ fontWeight: 800, fontSize: 26, letterSpacing: -0.5 }}>Sign in</Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 14.5, mt: .6, mb: 3.5 }}>
                        Enter your email and password to continue.
                    </Typography>

                    <Stack spacing={2.5}>
                        <TextField label="Email" size="small" fullWidth sx={darkField} autoFocus
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            onKeyDown={(e) => e.key === "Enter" && submit()} />
                        <TextField label="Password" size="small" fullWidth sx={darkField}
                            type={show ? "text" : "password"} value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            onKeyDown={(e) => e.key === "Enter" && submit()}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setShow(!show)}
                                                sx={{ color: "rgba(255,255,255,.5)" }}>
                                                {show ? <VisibilityOffIcon fontSize="small" />
                                                    : <VisibilityIcon fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }} />
                        <Button variant="contained" onClick={submit}
                            disabled={busy || !form.email || !form.password}
                            sx={{
                                borderRadius: 999, py: 1.1, fontWeight: 700,
                            }}
                            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
                            {busy ? "Signing in…" : "Sign in"}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
}
